import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { navItems } from "@/mocks/navigation";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const prevMobileOpen = useRef(false);
  const navigate = useNavigate();

  const handleEnter = (label: string) => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenDropdown(label);
  };

  const handleLeave = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
    }
    closeTimer.current = window.setTimeout(() => {
      setOpenDropdown(null);
      closeTimer.current = null;
    }, 200);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        setOpenDropdown(null);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  // Move keyboard/screen-reader focus into the mobile navigation when it opens,
  // and return focus to the menu button when it closes.
  useEffect(() => {
    if (mobileOpen && !prevMobileOpen.current) {
      const first = mobileNavRef.current?.querySelector<HTMLElement>("a, button, [tabindex]");
      first?.focus();
    } else if (!mobileOpen && prevMobileOpen.current) {
      menuButtonRef.current?.focus();
    }
    prevMobileOpen.current = mobileOpen;
  }, [mobileOpen]);

  const handleNav = (href: string) => {
    setMobileOpen(false);
    setOpenDropdown(null);
    navigate(href);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-background-200/80"
          : "bg-transparent"
      }`}
    >
      <div className="container-page">
        <div className="flex items-center justify-between h-18 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-3"
            onClick={() => { setMobileOpen(false); setOpenDropdown(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          >
            <img
              src="https://storage.helloreaddy.io/project_files/1b8b6e18-bd05-4772-a2fe-587c059d8c1c/e39c702c-5fea-44a6-8eee-f20d16407243_compressed_mitaja-header-logo-dark.webp"
              alt="Mitaja"
              className="h-9 md:h-10 w-auto object-contain"
            />
          </Link>

          <nav
            aria-label="Primary"
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpenDropdown(null);
            }}
            className="hidden lg:flex items-center gap-1"
          >
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && handleEnter(item.label)}
                onMouseLeave={handleLeave}
              >
                <button
                  onClick={() => handleNav(item.href)}
                  onFocus={() => item.children && setOpenDropdown(item.label)}
                  onKeyDown={(e) => { if (e.key === "Escape") setOpenDropdown(null); }}
                  aria-haspopup={item.children ? "true" : undefined}
                  aria-expanded={item.children ? openDropdown === item.label : undefined}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    scrolled
                      ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {item.label}
                  {item.children && (
                    <i className="ri-arrow-down-s-line ml-1 text-xs" aria-hidden="true"></i>
                  )}
                </button>
                {item.children && openDropdown === item.label && (
                  <div className="absolute top-full left-0 pt-1 w-72 z-50">
                    <div className="bg-white rounded-lg border border-background-200/80 shadow-lg py-2">
                      {item.children.map((child) => {
                        const isExternal = child.href.startsWith("http");
                        return isExternal ? (
                          <a
                            key={child.label}
                            href={child.href}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="block w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                            onClick={() => setOpenDropdown(null)}
                          >
                            {child.label}
                          </a>
                        ) : (
                          <button
                            key={child.label}
                            onClick={() => handleNav(child.href)}
                            className="block w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                          >
                            {child.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <button
            ref={menuButtonRef}
            className={`lg:hidden p-2 rounded-md cursor-pointer ${scrolled ? "text-foreground-950" : "text-white"}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div id="mobile-nav" aria-label="Mobile menu" className="lg:hidden fixed inset-0 top-18 bg-white z-40 overflow-y-auto" ref={mobileNavRef}>
          <div className="px-4 py-6 space-y-1">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-background-100 last:border-0">
                <button
                  onClick={() => {
                    if (item.children) {
                      setOpenDropdown(openDropdown === item.label ? null : item.label);
                    } else {
                      handleNav(item.href);
                    }
                  }}
                  className="flex items-center justify-between w-full py-4 text-left text-foreground-800 font-medium text-base cursor-pointer"
                >
                  {item.label}
                  {item.children && (
                    <i className={`ri-arrow-down-s-line transition-transform ${openDropdown === item.label ? "rotate-180" : ""}`}></i>
                  )}
                </button>
                {item.children && openDropdown === item.label && (
                  <div className="pb-3 space-y-1">
                    {item.children.map((child) => {
                      const isExternal = child.href.startsWith("http");
                      return isExternal ? (
                        <a
                          key={child.label}
                          href={child.href}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="block w-full text-left pl-4 py-2.5 text-sm text-foreground-600 hover:text-primary-700"
                        >
                          {child.label}
                        </a>
                      ) : (
                        <button
                          key={child.label}
                          onClick={() => handleNav(child.href)}
                          className="block w-full text-left pl-4 py-2.5 text-sm text-foreground-600 hover:text-primary-700 cursor-pointer"
                        >
                          {child.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}